import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../utils/api';
import { getBuildingNameClass, getSsDisplayClass } from '../../utils/ssDueColor';
import { useAuthStore } from '../../stores/authStore';
import { Award, ArrowUpDown, ClipboardList, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import SsDefaultAssignee from '../../components/SsDefaultAssignee';
import { daysUntilDue } from '../../utils/dueDate';
import { JOB_STATUS_LABELS } from '../../utils/jobStatusColor';
import { getSsResultCardClass } from '../../utils/ssInspectionTone';
import {
  ISSUE_LABELS,
} from '../../constants/jobModel';
import { compareSsCode } from '../../utils/ssCodeSort';
import { filterInstancesForInspectionScope } from '../../utils/inspectionScope';
import { asList } from '../../utils/asList';
import {
  toYmd,
  pickOpenJob,
  createInspectionJob,
  orderMissingInspections,
} from '../../utils/orderInspectionJobs';

const CLOSED = new Set(['COMPLETED', 'REJECTED']);

function SsOrderCard({
  inst,
  cardClass,
  ssTextClass,
  buildingId,
  existingJob,
  onCreate,
  creating,
}) {
  const navigate = useNavigate();

  const openJob = existingJob && !CLOSED.has(existingJob.status) ? existingJob : null;
  const closedJob = existingJob && CLOSED.has(existingJob.status) ? existingJob : null;

  return (
    <div className={`rounded-xl overflow-hidden shadow-sm border-2 ${cardClass}`}>
      <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg font-semibold ${ssTextClass}`}>
          <Award className="w-4 h-4" />
          {inst.ss_code}
          {inst.ss_name && inst.ss_name !== inst.ss_code && (
            <span className="opacity-90 text-sm">({inst.ss_name})</span>
          )}
        </span>
        {inst.nextDueDate && (
          <span className="text-sm text-gray-600">
            Due: {new Date(inst.nextDueDate).toLocaleDateString()}
          </span>
        )}
        <div className="ml-auto shrink-0 flex items-center gap-2 flex-wrap justify-end">
          <SsDefaultAssignee
            compact
            buildingId={buildingId}
            instanceId={inst.id}
            ssCode={inst.ss_code}
            instancePack={inst}
          />
          {openJob ? (
            <button
              type="button"
              onClick={() => navigate(`/jobs/${openJob.id}`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Open inspection
              <span className="opacity-90 font-normal">
                ({JOB_STATUS_LABELS[openJob.status] || openJob.status})
              </span>
            </button>
          ) : (
            <button
              type="button"
              disabled={creating || !toYmd(inst.nextDueDate)}
              onClick={() => onCreate(inst)}
              title={!toYmd(inst.nextDueDate) ? 'Set SS due date first' : 'Issue a work pack for this SS'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <ClipboardList className="w-3.5 h-3.5" />
              {closedJob ? ISSUE_LABELS.next : ISSUE_LABELS.one}
            </button>
          )}
        </div>
      </div>
      {!toYmd(inst.nextDueDate) && (
        <p className="px-4 pb-3 text-xs text-amber-700">
          No due date on this SS ?? set it in Building edit before issuing.
        </p>
      )}
    </div>
  );
}

function BuildingOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: building, isLoading, isError, error } = useQuery({
    queryKey: ['building', id],
    queryFn: async () => {
      const response = await api.get(`/buildings/${id}`);
      return response.data;
    },
    retry: false,
  });

  const { data: buildingJobs = [] } = useQuery({
    queryKey: ['jobs', 'building', id],
    queryFn: async () => {
      const response = await api.get('/jobs', {
        params: { buildingId: id, limit: 200 },
      });
      return asList(response.data?.data ?? response.data);
    },
    enabled: !!id,
  });

  const { data: unitStates = [] } = useQuery({
    queryKey: ['building', id, 'unit-inspection-states'],
    queryFn: async () => {
      const res = await api.get(`/buildings/${id}/unit-inspection-states`);
      return asList(res.data?.data ?? res.data);
    },
    enabled: !!id,
    staleTime: 15_000,
  });

  const { data: ssUnits = [] } = useQuery({
    queryKey: ['building', id, 'ss-units', 'for-tone'],
    queryFn: async () => {
      const res = await api.get(`/buildings/${id}/ss-units`);
      return asList(res.data?.data ?? res.data);
    },
    enabled: !!id,
    staleTime: 60_000,
  });

  const unitToneBySs = useMemo(() => {
    const statesBySs = new Map();
    (unitStates || []).forEach((s) => {
      const code = String(s.ss_code || '').trim();
      if (!code) return;
      if (!statesBySs.has(code)) statesBySs.set(code, []);
      statesBySs.get(code).push(s);
    });
    const countBySs = new Map();
    (ssUnits || []).forEach((u) => {
      if (u?.status === 'INACTIVE') return;
      const code = String(u.ss_code || '').trim();
      if (!code) return;
      countBySs.set(code, (countBySs.get(code) || 0) + 1);
    });
    return { statesBySs, countBySs };
  }, [unitStates, ssUnits]);

  const jobsBySs = useMemo(() => {
    const map = new Map();
    asList(buildingJobs).forEach((j) => {
      const key = j.ss_code || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(j);
    });
    return map;
  }, [buildingJobs]);

  const [ssSortBy, setSsSortBy] = useState('ss');
  const [ssSortOrder, setSsSortOrder] = useState('asc');
  const rawSsList = filterInstancesForInspectionScope(
    building?.ssInstancesInScope ||
      building?.ssInstances?.filter((i) => i.status === 'ACTIVE') ||
      [],
    building
  );
  const ssList = [...rawSsList].sort((a, b) => {
    const mul = ssSortOrder === 'asc' ? 1 : -1;
    if (ssSortBy === 'date') {
      const dA = a.nextDueDate ? new Date(a.nextDueDate).getTime() : 0;
      const dB = b.nextDueDate ? new Date(b.nextDueDate).getTime() : 0;
      return mul * (dA - dB);
    }
    return mul * compareSsCode(a.ss_code, b.ss_code);
  });

  const createOne = async (inst) => {
    const due = toYmd(inst.nextDueDate);
    if (!due) {
      toast.error(`No due date for ${inst.ss_code}`);
      return null;
    }
    return createInspectionJob({
      buildingId: id,
      ssCode: inst.ss_code,
      ssName: inst.ss_name || inst.ss_code,
      dueDate: due,
      inspectionFrequency: inst.inspection_frequency || inst.inspectionFrequency || 'ANNUAL',
      assignedWorkerId: inst.default_worker_id || null,
    });
  };

  const createMutation = useMutation({
    mutationFn: async (inst) => createOne(inst),
    onSuccess: (data, inst) => {
      toast.success(`Issued inspection for ${inst.ss_code}`);
      queryClient.invalidateQueries(['jobs']);
      queryClient.invalidateQueries(['jobs', 'building', id]);
      if (data?.id) navigate(`/jobs/${data.id}`);
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to issue inspection'),
  });

  const createAllMissingMutation = useMutation({
    mutationFn: async () => {
      const missing = ssList.filter((inst) => {
        const open = pickOpenJob(jobsBySs.get(inst.ss_code) || []);
        return !(open && !CLOSED.has(open.status)) && toYmd(inst.nextDueDate);
      });
      if (!missing.length) return { created: 0 };
      return orderMissingInspections({ buildingId: id, items: missing });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries(['jobs']);
      queryClient.invalidateQueries(['jobs', 'building', id]);
      if (!result.created) {
        toast('All SS already have an open pack (or missing due dates)');
      } else {
        toast.success(`Issued ${result.created} inspection(s)`);
      }
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to issue inspections'),
  });

  const missingCount = ssList.filter((inst) => {
    const job = pickOpenJob(jobsBySs.get(inst.ss_code) || []);
    return !(job && !CLOSED.has(job.status)) && toYmd(inst.nextDueDate);
  }).length;

  if (user?.role === 'WORKER') {
    navigate('/', { replace: true });
    return null;
  }

  if (isLoading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  if (isError || !building) {
    return (
      <div>
        <BuildingPageHeading buildingId={id} title={ISSUE_LABELS.pageTitle} />
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-red-600 font-medium">{error?.response?.data?.error || 'Building not found'}</p>
          <p className="text-sm text-gray-500 mt-2">Building ID: {id}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <BuildingPageHeading
        buildingId={id}
        buildingName={building?.name}
        title={ISSUE_LABELS.pageTitle}
        infoTitle={ISSUE_LABELS.pageTitle}
        infoChildren={
          <div className="space-y-2 text-sm">
            <p className={`font-medium ${getBuildingNameClass(daysUntilDue(building?.due_date), building?.application_approved)}`}>
              {building?.name}
            </p>
            {building.address ? <p>{building.address}</p> : null}
            <p>{ISSUE_LABELS.hint}</p>
            <p>
              Set the default IQP chip if needed, then <strong>{ISSUE_LABELS.one}</strong> for that SS. That creates the
              work pack on Inspections.
            </p>
          </div>
        }
        actions={
          <div className="flex flex-col gap-2 items-stretch sm:items-end">
            <button
              type="button"
              disabled={createAllMissingMutation.isPending || missingCount === 0}
              onClick={() => createAllMissingMutation.mutate()}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <ClipboardList className="w-4 h-4" />
              {createAllMissingMutation.isPending
                ? 'Issuing…'
                : ISSUE_LABELS.allMissing(missingCount)}
            </button>
            <button
              type="button"
              onClick={() => navigate(`/jobs/new?building_id=${id}`)}
              className="text-sm text-primary-700 hover:underline"
            >
              Or open New Inspection form??            </button>
          </div>
        }
      />

      {ssList.length > 1 && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Sort by:</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setSsSortBy('ss'); if (ssSortBy === 'ss') setSsSortOrder((o) => (o === 'asc' ? 'desc' : 'asc')); else setSsSortOrder('asc'); }}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border ${ssSortBy === 'ss' ? 'border-primary-500 bg-primary-50 text-primary-800' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              SS code
              {ssSortBy === 'ss' && <ArrowUpDown className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              onClick={() => { setSsSortBy('date'); if (ssSortBy === 'date') setSsSortOrder((o) => (o === 'asc' ? 'desc' : 'asc')); else setSsSortOrder('asc'); }}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm border ${ssSortBy === 'date' ? 'border-primary-500 bg-primary-50 text-primary-800' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              Due date
              {ssSortBy === 'date' && <ArrowUpDown className="w-3.5 h-3.5" />}
            </button>
          </div>
          <span className="text-xs text-gray-500">
            {ssSortOrder === 'asc' ? 'Ascending' : 'Descending'}
          </span>
        </div>
      )}

      {ssList.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-gray-500">
          No active SS registered for this building. Add SS in building edit.
        </div>
      ) : (
        <div className="space-y-4">
          {ssList.map((inst) => {
            const days = daysUntilDue(inst.nextDueDate);
            const code = String(inst.ss_code || '').trim();
            const jobsForSs = jobsBySs.get(inst.ss_code) || [];
            const existingJob = pickOpenJob(jobsForSs);
            const closedJob = jobsForSs.find((j) => CLOSED.has(j.status)) || null;
            const jobForTone = existingJob || closedJob || jobsForSs[0] || null;
            const cardClass = getSsResultCardClass({
              jobStatus: jobForTone?.status || null,
              unitStates: unitToneBySs.statesBySs.get(code) || [],
              expectedUnitCount: unitToneBySs.countBySs.get(code) || 0,
            });
            const resultPass = cardClass.includes('green');
            const ssTextClass = getSsDisplayClass(days, resultPass, inst?.missed);
            return (
              <SsOrderCard
                key={inst.id}
                inst={inst}
                cardClass={cardClass}
                ssTextClass={ssTextClass}
                buildingId={id}
                existingJob={existingJob}
                creating={createMutation.isPending}
                onCreate={(row) => createMutation.mutate(row)}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default BuildingOrder;
